import api from '../services/api';
import { checkIsYouTubeShorts } from './common/errorParser';
import { Capacitor } from '@capacitor/core';

/**
 * YouTube API를 통해 실시간 방송 여부를 체크합니다.
 * @param {string} videoId - YouTube 비디오 ID
 * @returns {Promise<{isLiveStream: boolean, liveStreamMessage?: string}>}
 */
const checkLiveStreamStatus = async (videoId) => {
  try {
    console.log('🔍 [실시간 체크] YouTube API 호출:', videoId);
    
    const response = await api.get(`/api/video-info/${videoId}`);
    
    if (response.data && response.data.success && response.data.data) {
      const videoInfo = response.data.data;
      const liveBroadcastContent = videoInfo.live_broadcast_content;
      
      console.log('📺 [실시간 체크] liveBroadcastContent:', liveBroadcastContent);
      
      if (liveBroadcastContent === 'live') {
        return {
          isLiveStream: true,
          liveStreamMessage: '이 영상은 현재 실시간 방송 중입니다. 실시간 방송은 요약할 수 없습니다. 방송이 종료된 후 다시 시도해주세요.'
        };
      } else if (liveBroadcastContent === 'upcoming') {
        return {
          isLiveStream: true,
          liveStreamMessage: '이 영상은 예정된 라이브 방송입니다. 라이브 방송은 요약할 수 없습니다. 방송이 종료된 후 다시 시도해주세요.'
        };
      }
    }
    
    console.log('✅ [실시간 체크] 일반 영상 확인');
    return { isLiveStream: false };
    
  } catch (error) {
    console.warn('⚠️ [실시간 체크] 체크 실패 (계속 진행):', error);
    return { isLiveStream: false };
  }
};

/**
 * 서버 기반 사전 체크 (웹 전용): 쇼츠 감지 → 실시간 방송 감지 → 자막 존재 여부 체크
 * @param {string} videoId - YouTube 비디오 ID
 * @param {string} videoUrl - YouTube 비디오 URL
 * @returns {Promise<Object>} 사전 체크 결과
 * @throws {Error} 쇼츠, 실시간 방송, 자막 없음 감지 시 에러
 */
export const performServerPreAnalysisCheck = async (videoId, videoUrl) => {
  console.log('🔍 [서버 사전 체크] 시작:', { videoId, videoUrl });
  
  try {
    // 1. 쇼츠 감지 (가장 먼저 체크)
    console.log('📱 [서버 사전 체크] 쇼츠 여부 확인 중...');
    if (checkIsYouTubeShorts(videoUrl)) {
      const errorMessage = 'YouTube 쇼츠는 일반적으로 자막이 없어 분석이 어렵습니다.';
      console.log('❌ [서버 사전 체크] 쇼츠 감지:', errorMessage);
      throw new Error(`YOUTUBE_SHORTS: ${errorMessage}`);
    }
    console.log('✅ [서버 사전 체크] 쇼츠 체크 통과');
    
    // 2. 실시간 방송 감지 (서버 → YouTube API)
    console.log('📺 [서버 사전 체크] 실시간 방송 여부 확인 중...');
    const liveStreamCheck = await checkLiveStreamStatus(videoId);
    
    if (liveStreamCheck.isLiveStream) {
      console.log('❌ [서버 사전 체크] 실시간 방송 감지:', liveStreamCheck.liveStreamMessage);
      throw new Error(`LIVE_STREAM: ${liveStreamCheck.liveStreamMessage}`);
    }
    console.log('✅ [서버 사전 체크] 실시간 방송 체크 통과');
    
    // 3. 자막 존재 여부 체크 (서버 → 윈도우랩탑서버)
    console.log('📋 [서버 사전 체크] 자막 존재 여부 확인 중...');
    const captionCheckResponse = await api.get(`/api/analyze/check-captions/${videoId}`);
    const captionCheck = captionCheckResponse.data;
    
    console.log('✅ [서버 사전 체크] 자막 체크 결과:', captionCheck);
    
    // 자막이 없는 경우 (실시간 방송이 아닌 경우)
    if (!captionCheck.hasCaption) {
      const errorMessage = captionCheck.message || '이 영상에는 사용 가능한 자막이 없습니다.';
      console.log('❌ [서버 사전 체크] 자막 없음 감지:', errorMessage);
      throw new Error(`NO_CAPTIONS: ${errorMessage}`);
    }
    
    console.log('✅ [서버 사전 체크] 모든 체크 통과');
    return {
      hasCaptions: true,
      isLiveStream: false,
      message: '서버 사전 체크 통과',
      source: 'server'
    };
    
  } catch (error) {
    console.error('❌ [서버 사전 체크] 에러 발생:', error);
    
    // 이미 형식화된 에러 메시지인 경우 그대로 던짐
    if (error.message.startsWith('YOUTUBE_SHORTS:') || 
        error.message.startsWith('LIVE_STREAM:') || 
        error.message.startsWith('NO_CAPTIONS:')) {
      throw error;
    }
    
    // 네트워크 에러 등 기타 에러
    const errorMessage = error.response?.data?.detail || error.message || '서버 사전 체크 중 오류가 발생했습니다.';
    throw new Error(`SERVER_PRECHECK_ERROR: ${errorMessage}`);
  }
};

/**
 * 플랫폼을 자동 감지하여 적절한 사전체크 방식을 선택합니다.
 * - 모바일 (iOS/Android): 네이티브 클라이언트 사전체크
 * - 웹: 서버 기반 사전체크
 * 
 * @param {string} videoId - YouTube 비디오 ID
 * @param {string} videoUrl - YouTube 비디오 URL
 * @returns {Promise<Object>} 사전 체크 결과
 * @throws {Error} 쇼츠, 실시간 방송, 자막 없음 감지 시 에러
 */
export const performPreAnalysisCheck = async (videoId, videoUrl) => {
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();
  
  console.log('🔍 [플랫폼 감지] 플랫폼:', platform, '네이티브:', isNative);
  
  if (isNative && (platform === 'ios' || platform === 'android')) {
    // 모바일: 네이티브 클라이언트 사전체크
    console.log('📱 [플랫폼 감지] 모바일 네이티브 사전체크 사용');
    const { performNativePreAnalysisCheck } = await import('../services/nativePreAnalysisCheck');
    return await performNativePreAnalysisCheck(videoId, videoUrl);
  } else {
    // 웹: 기존 서버 사전체크
    console.log('🌐 [플랫폼 감지] 웹 서버 사전체크 사용');
    return await performServerPreAnalysisCheck(videoId, videoUrl);
  }
}; 